import { DataSyncerConfig, getBackendPushUrl, getApiBaseOrigin } from '@/BuildConfig'
import {
  ActionNoticeDataBuilder,
  AsyncOperationEventDataBuilder,
  NoticeFactory,
  TransmissionNoticeDataBuilder,
} from '@core/databus/NoticeDataBuilders'
import type { Value as DfValue } from '@core/UCloudDataWebSDK/cloud-data/network/Model'
import { dataSyncer } from '@core/UCloudDataWebSDK/cloud-data/syncer/DataSyncer'
import { logger } from '@core/UCloudDataWebSDK/cloud-data/utils/log'

import type { IDataSyncManager, INoticeListener, INoticeResult } from '@core/databus/IDataSyncManager'
import { NoticeOperationResult } from '@core/databus/IDataSyncManager'
import type {
  ActionNotice,
  AsyncOperationEvent,
  Notice,
  TransmissionNotice,
  TransmissionInfo,
} from '@core/databus/IMessage'
import { NoticeCategory } from '@core/databus/IMessage'
import { CloudAdapter } from '@/netadapters/CloudAdapter'
import { v4 as uuidV4 } from 'uuid'
import { composeErrorData, composeFailedData, composeSuccessData, getTimestamp } from '@core/databus/NoticeDataUtils'
import type { EventCallbackAggregator, EventCallbackGroup } from '@core/databus/DataSyncEventChangeListener'
import { DataSyncEventChangeListener } from '@core/databus/DataSyncEventChangeListener'
import type { OnDataChangeListener } from '@core/UCloudDataWebSDK/cloud-data/syncer/DataSyncer'
import { retry, TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES } from '@/core/decorators/RetryDecorator'
import { isPlainObject } from 'lodash'

const { VITE_LOG_ENABLED } = import.meta.env

logger.setDebug(JSON.parse(VITE_LOG_ENABLED))

/**
 * 通知数据基础类型
 */
export interface BaseNoticeData {
  /**
   *  通知的唯一ID（在开发运行框架中的中台topic中唯一）
   */
  id: string
  /**
   * 消息类型
   *
   * 1：消息通知 2：传输 3：异步动作通知
   */
  category: NoticeCategory
  /**
   * 时间戳
   */
  timestamp: number
}

export interface AsyncOperationEventData extends AsyncOperationEvent, BaseNoticeData {}

export interface NoticeData extends Notice, BaseNoticeData {}

export interface TransmissionNoticeData extends TransmissionNotice, BaseNoticeData, TransmissionInfo {}

export interface ActionNoticeData extends ActionNotice, BaseNoticeData {}

/**
 * @deprecated
 */
export type NoticeDataType = AsyncOperationEventData | NoticeData | TransmissionNoticeData | ActionNoticeData

/**
 * For type reference use {@link IDataSyncManager} instead
 * @internal
 */
export class DataSyncManager implements IDataSyncManager, EventCallbackAggregator {
  #actionNoticeListenerSet = new Set<INoticeListener<ActionNoticeData>>()

  #transmissionNoticeListenerSet = new Set<INoticeListener<TransmissionNoticeData>>()

  #asyncEventListenerSet = new Set<INoticeListener<AsyncOperationEventData>>()

  #webClientId = uuidV4()
  adapter: CloudAdapter = new CloudAdapter()

  // #dataCache = new NoticeDataCache(this)
  // 监听sdk消息通知
  #eventDispatcher = new DataSyncEventChangeListener(this)
  #syncEventListener: OnDataChangeListener<DfValue> = {
    onDataAdded: (packageName, value) => {
      console.log(`[DataSyncManager] ${new Date().toLocaleString()} `, '收到了消息事件onDataAdded数据为:', value)
      this.#eventDispatcher.onDataAdded(packageName, value)

      // const liteValue = this.#dataCache.getAddedByRemote(value)

      // this.#dataCache.isInitialized && this.#eventDispatcher.onDataAdded(packageName, liteValue)
    },
    // onDataChanged: (packageName, value) => {
    //   console.log(`[DataSyncManager] ${new Date().toLocaleString()} `, '收到了消息事件onDataChanged数据为:', value)

    //   // const liteValue = this.#dataCache.getUpdatedByRemote(value)

    //   // this.#dataCache.isInitialized && this.#eventDispatcher.onDataChanged(packageName, liteValue)
    // },
    // onDataRemoved: (packageName, value) => {
    //   console.log(`[DataSyncManager] ${new Date().toLocaleString()} `, '收到了消息事件onDataRemoved数据为:', value)

    //   // const liteValue = this.#dataCache.getDeletedByRemote(value)

    //   // this.#dataCache.isInitialized && this.#eventDispatcher.onDataRemoved(packageName, liteValue)
    // },
  }

  /**
   * For test convenience only. For production version, use {@link INSTANCE} directly.
   */
  constructor() {
    // default constructor
  }

  get webClientId(): string {
    return this.#webClientId
  }

  init() {
    // init async websocket
    this.initDataSync()
    this.#notifyInitialized()
  }

  /**
   * 初始化 async data
   * @returns
   */
  private initDataSync(): void {
    try {
      dataSyncer.addDataChangeListener(DataSyncerConfig.TOPIC_KEY, this.#syncEventListener)

      const wsAddress: string = getBackendPushUrl() || 'wss://owork-api-cn-test.wanyol.com/ws-owork-server'
      const apiAddress: string = getApiBaseOrigin() || 'https://owork-api-cn-test.wanyol.com'

      dataSyncer.init({
        wsAddress,
        apiAddress,
        retries: 10, //重试次数
      })
    } catch (error) {
      console.log(`[DataSyncManager] ${new Date().toLocaleString()} `, 'dataSyncer init failed', error)
    }
  }

  #notifyInitialized(): void {
    for (const listener of this.#actionNoticeListenerSet) {
      listener.onInitialized()
    }

    for (const listener of this.#asyncEventListenerSet) {
      listener.onInitialized()
    }

    for (const listener of this.#transmissionNoticeListenerSet) {
      listener.onInitialized()
    }
  }

  static validateResult(result: unknown) {
    const isNoticeResult = (result: unknown): result is { status: unknown } =>
      isPlainObject(result) && 'status' in (result as object)

    return isNoticeResult(result) ? result.status === NoticeOperationResult.SUCCESS : false
  }

  /**
   * 同步通知信息 ,通过http 请求服务端
   * @param sendData
   * @returns
   */
  @retry(DataSyncManager.validateResult)
  private syncData<T extends BaseNoticeData>(sendData: T): Promise<INoticeResult<T>> {
    return new Promise((resolve, reject) => {
      try {
        // TODO: 后续需要调整为对象内部提供toString和getTimestamp方法来调用的形式来运行
        delete (sendData as Record<string, unknown>).timestamp

        const data = new Map([[sendData.id, JSON.stringify(sendData)]])

        dataSyncer.syncData(data, {
          onSuccess: (topic: string, request: any, response: Response) => {
            console.log(`[DataSyncManager] ${new Date().toLocaleString()} `, '消息发送成功:', JSON.stringify(sendData))
            resolve(composeSuccessData<T>(sendData))
          },
          onFailure: () => {
            reject(composeFailedData<undefined>('failed to syncData'))
          },
          onError: () => {
            reject(composeErrorData<undefined>('syncData error'))
          },
        })
      } catch (error) {
        console.log(`[DataSyncManager] ${new Date().toLocaleString()} `, 'send data error:', JSON.stringify(sendData))
        reject(composeErrorData<undefined>('syncData error'))
      }
    })
  }

  // 删除id对应的通知
  async removeNoticeById(id: string): Promise<INoticeResult<boolean>> {
    return this.removeNoticesByIds([id])
  }

  @retry(DataSyncManager.validateResult)
  removeNoticesByIds(ids: string[]): Promise<INoticeResult<boolean>> {
    return new Promise((resolve, reject) => {
      try {
        dataSyncer.removeData(ids, {
          onSuccess: () => {
            resolve(composeSuccessData<boolean>(true))
          },
          onFailure: () => {
            reject(composeFailedData<boolean>('fail to delete!', false))
          },
          onError: () => {
            reject(composeErrorData<boolean>('delete error!', false))
          },
        })
      } catch (error) {
        reject(composeFailedData<boolean>('fail to delete!', false))
      }
    })
  }

  async removeNoticeByCategory<T extends BaseNoticeData>(category: NoticeCategory): Promise<INoticeResult<boolean>> {
    const noticeList = await this.getAllNoticesByCategory<T>(category)

    if (noticeList.data !== undefined) {
      const ids = noticeList.data.map((item) => item.id)

      return this.removeNoticesByIds(ids)
    } else {
      return composeFailedData('fail to delete!', false)
    }
  }

  // 获取id对应的通知
  async getNoticeById<T>(id: string): Promise<INoticeResult<T>> {
    return new Promise((resolve, reject) => {
      //换成接口查
      try {
        dataSyncer.getDataByGlobalMessageId(id, {
          onSuccess: (topic: string, request: any, response: any) => {
            console.log('查询消息成功', topic, request, response)
            resolve(composeSuccessData<T>(JSON.parse(response.data.data) as T))
          },
          onFailure: () => {
            reject(composeFailedData<undefined>('failed to getNotice'))
          },
          onError: () => {
            reject(composeErrorData<undefined>('getNotice error'))
          },
        })
      } catch (error) {
        reject(composeFailedData<undefined>('failed to getNotice'))
      }
    })
  }

  // 编辑id对应的通知
  async updateNoticeById<T extends BaseNoticeData>(id: string, data: Partial<T>): Promise<INoticeResult<T>> {
    const notice = await this.getNoticeById<T>(id)

    if (notice.data !== undefined) {
      const syncData = { ...notice.data, ...data } as T

      return this.syncData<T>(syncData)
    } else {
      return composeFailedData('failed to update!')
    }
  }

  async appendActionNotice(notice: ActionNotice): Promise<INoticeResult<ActionNoticeData>> {
    const id = this.getNoticeIdentity()

    if (id) {
      const timestamp = getTimestamp(id)
      const builder = new ActionNoticeDataBuilder()
      const messageData = builder.buildUpon({ id, timestamp, category: NoticeCategory.NOTICE, ...notice }).build()

      return messageData ? this.syncData(messageData) : composeFailedData('build noticeData failed')
    } else {
      return composeFailedData('get id failed')
    }
  }

  @retry(DataSyncManager.validateResult, undefined, TRANSMISSION_MAXIMUM_NUMBER_OF_RETRIES)
  async appendTransmissionNotice(notice: TransmissionNotice): Promise<INoticeResult<TransmissionNoticeData>> {
    const id = this.getNoticeIdentity()

    if (id) {
      const timestamp = getTimestamp(id)

      const builder = new TransmissionNoticeDataBuilder()
      const messageData = builder
        .buildUpon({ id, timestamp, category: NoticeCategory.TRANSMISSION, webClientId: this.#webClientId, ...notice })
        .build()

      return messageData ? this.syncData(messageData) : composeFailedData('build noticeData failed')
    } else {
      return composeFailedData('get id failed')
    }
  }

  async appendAsyncOperationEvent(notice: AsyncOperationEvent): Promise<INoticeResult<AsyncOperationEventData>> {
    const id = this.getNoticeIdentity()

    if (id) {
      const timestamp = getTimestamp(id)

      const builder = new AsyncOperationEventDataBuilder()
      const messageData = builder.buildUpon({ id, timestamp, category: NoticeCategory.EVENT, ...notice }).build()

      return messageData ? this.syncData(messageData) : composeFailedData('build noticeData failed')
    } else {
      return composeFailedData('get id failed')
    }
  }

  @retry(DataSyncManager.validateResult)
  getAllNotices(category: NoticeCategory): Promise<INoticeResult<[]>> {
    return new Promise((resolve, reject) => {
      try {
        dataSyncer.getDataByNotifyType(category + '', {
          onSuccess: (topic, request, response) => {
            //原始代码debug  TODO
            resolve(composeSuccessData<[]>(response.data))
          },
          onFailure: () => {
            resolve(composeSuccessData<[]>())
          },
          onError: () => {
            resolve(composeSuccessData<[]>())
          },
        })
      } catch (error) {
        resolve(composeSuccessData<[]>())
      }
    })
  }

  async getAllNoticesByCategory<T extends BaseNoticeData>(category: NoticeCategory): Promise<INoticeResult<T[]>> {
    return new Promise(async (resolve, reject) => {
      // const noticeData = this.#dataCache.getAll()
      const obj: INoticeResult<[]> = await this.getAllNotices(category)
      const list: any[] = []
      const resultData = obj?.data?.map((noticeStr: any) => {
        try {
          const resultData = JSON.parse(noticeStr?.data)

          if (resultData.id) {
            resultData.timestamp = getTimestamp(resultData.id)
          }
          const targetCategory = category ? category : resultData?.category
          const builder = NoticeFactory.getBuilder(targetCategory)

          if (builder) {
            const resultNotice = builder.fromJson(resultData).build()

            resultNotice && list.push(resultNotice)
          }
        } catch (error) {
          console.log(error)
        }
      })

      resolve(composeSuccessData<T[]>(list as unknown as T[]))
    })
  }

  addNoticeListener<T extends AsyncOperationEventData | NoticeData | TransmissionNoticeData | ActionNoticeData>(
    category: NoticeCategory,
    listener: INoticeListener<T>
  ): void {
    // this.#dataCache.isInitialized && listener.onInitialized()

    switch (category) {
      case NoticeCategory.NOTICE:
        if (!this.#actionNoticeListenerSet.has(<INoticeListener<ActionNoticeData>>listener)) {
          this.#actionNoticeListenerSet.add(<INoticeListener<ActionNoticeData>>listener)
        }

        break
      case NoticeCategory.TRANSMISSION:
        if (!this.#transmissionNoticeListenerSet.has(<INoticeListener<TransmissionNoticeData>>listener)) {
          this.#transmissionNoticeListenerSet.add(<INoticeListener<TransmissionNoticeData>>listener)
        }

        break
      case NoticeCategory.EVENT:
        if (!this.#asyncEventListenerSet.has(<INoticeListener<AsyncOperationEventData>>listener)) {
          this.#asyncEventListenerSet.add(<INoticeListener<AsyncOperationEventData>>listener)
        }
        break
      default:
        break
    }
  }

  removeNoticeListener<T extends AsyncOperationEventData | NoticeData | TransmissionNoticeData | ActionNoticeData>(
    category: NoticeCategory,
    listener: INoticeListener<T>
  ): boolean {
    switch (category) {
      case NoticeCategory.NOTICE:
        return this.#actionNoticeListenerSet.delete(<INoticeListener<ActionNoticeData>>listener)

      case NoticeCategory.TRANSMISSION:
        return this.#transmissionNoticeListenerSet.delete(<INoticeListener<TransmissionNoticeData>>listener)
      case NoticeCategory.EVENT:
        return this.#asyncEventListenerSet.delete(<INoticeListener<AsyncOperationEventData>>listener)
      default:
        return false
    }
  }

  destroy() {
    // this.#dataCache.destroy()
    dataSyncer.removeAllDataChangeListener()
  }

  /**
   * For test only. **DO NOT** use in production version.
   *
   * @deprecated
   * @internal
   */
  getDataSyncEventListener() {
    return this.#syncEventListener
  }

  /**
   * For test only. **DO NOT** use in production version.
   *
   * @deprecated
   * @internal
   */
  getDataCache() {
    // return this.#dataCache
  }

  /**
   * @override
   */
  getAllOnAddedCallbacks(): EventCallbackGroup {
    //遍历各类型监听set  并触发listener的方法
    return {
      actionNotice: Array.from(this.#actionNoticeListenerSet, (listener) => listener.onAdded),
      asyncOperationEvent: Array.from(this.#asyncEventListenerSet, (listener) => listener.onAdded),
      transmissionNotice: Array.from(this.#transmissionNoticeListenerSet, (listener) => listener.onAdded),
    }
  }

  /**
   * @override
   */
  getAllOnUpdatedCallbacks(): EventCallbackGroup {
    return {
      actionNotice: Array.from(this.#actionNoticeListenerSet, (listener) => listener.onUpdated),
      asyncOperationEvent: Array.from(this.#asyncEventListenerSet, (listener) => listener.onUpdated),
      transmissionNotice: Array.from(this.#transmissionNoticeListenerSet, (listener) => listener.onUpdated),
    }
  }

  /**
   * @override
   */
  getAllOnRemovedCallbacks(): EventCallbackGroup {
    return {
      actionNotice: Array.from(this.#actionNoticeListenerSet, (listener) => listener.onRemoved),
      asyncOperationEvent: Array.from(this.#asyncEventListenerSet, (listener) => listener.onRemoved),
      transmissionNotice: Array.from(this.#transmissionNoticeListenerSet, (listener) => listener.onRemoved),
    }
  }

  getNoticeIdentity(): string {
    return Date.now() + '_' + uuidV4()
  }
}

const INSTANCE = new DataSyncManager()

export { INSTANCE }
